import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const faqs = [
  {
    question: "How does the billing work?",
    answer:
      "Springerdata offers a variety of billing options, including monthly and annual subscription plans, as well as pay-as-you-go pricing for certain services.",
  },
  {
    question: "Can I get a refund for my subscription?",
    answer:
      "We offer a 30-day money-back guarantee for most of its subscription plans. Refunds after 30 days are considered case-by-case.",
  },
  {
    question: "How do I cancel my subscription?",
    answer:
      "You can cancel your subscription by logging into your account and navigating to the subscription management page.",
  },
  {
    question: "Is there a free trial?",
    answer:
      "Yes, we offer a limited-time free trial with access to selected features at no cost.",
  },
  {
    question: "How do I contact support?",
    answer:
      "You can contact support via email or by submitting a support ticket through our website.",
  },
  {
    question: "Do you offer any discounts or promotions?",
    answer:
      "We occasionally offer discounts and promotions. Stay updated via our newsletter and social media.",
  },
];

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.45,
      ease: "easeOut"
    }
  }
};

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section className="bg-gray-50 pt-14 lg:py-12 px-3 sm:px-6 lg:px-12 overflow-hidden">
           {/* ===== Heading ===== */}
            <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="text-center mb-8 lg:mb-10"
                    >
                      <h2 className="text-center text-3xl md:text-4xl font-extrabold tracking-tight text-transparent bg-clip-text bg-[var(--bg-primary)] drop-shadow-sm mb-2 lg:mb-2">
                         Frequently Asked Questions
                      </h2>
                      <div className="w-40 h-1 bg-[var(--bg-primary)] mx-auto rounded-full mb-2"></div>
                      <p className="text-sm md:text-sm text-gray-500 text-center max-w-md mx-auto">
                            Find quick answers to the most common questions about our services and processes.
                      </p>
                    </motion.div>
      <div className="max-w-[90vw] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 ">


        <motion.div
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{
                y: -4,
                boxShadow: "0px 15px 35px rgba(59,130,246,0.15)"
              }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="relative bg-white rounded-2xl border border-gray-100 overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <button
                onClick={() => toggleFAQ(index)}
                className="relative w-full flex items-center justify-between text-left py-3 px-4  md:p-6 focus:outline-none z-10"
              >
                <span className="text-sm md:text-lg font-medium text-gray-900 transition-colors duration-300 group-hover:text-[var(--text-primary)]">
                  {faq.question}
                </span>

                <motion.span
                  animate={{ rotate: activeIndex === index ? 180 : 0, scale: activeIndex === index ? 1.2 : 1 }}
                  transition={{ duration: 0.3 }}
                  className="ml-4 text-[var(--bg-primary)]"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </motion.span>
              </button>

              <AnimatePresence initial={false}>
                {activeIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="relative px-5 md:px-6 pb-5 md:pb-6 text-sm md:text-base text-gray-600 leading-relaxed border-t border-gray-100 z-10">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>

         {/* Right Visual */}
                <motion.div
                  initial={{ opacity: 0, x: 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="hidden md:block"
                >
                  <img
                    src="https://images.unsplash.com/photo-1551836022-d5d88e9218df"
                    alt="FAQs Illustration"
                    className="rounded-3xl shadow-2xl"
                  />
                  <div className="relative bottom-14 w-fit -left-6 bg-[var(--bg-primary)] text-white rounded-2xl p-6 shadow-xl">
                    <p className="text-lg font-bold">Need More Help?</p>
                    <p className="text-sm opacity-90">Talk to our experts anytime</p>
                  </div>
                </motion.div>
        

      </div>
    </section>
  );
};

export default FAQ;
